import { useEffect, useRef, useState } from "react"
import { ChevronUp, ChevronDown } from "lucide-react"
import type { CalendarEvent } from "../../types/agenda"
import { EVENT_COLORS, EVENT_COLORS_LIGHT, EVENT_TEXT_COLORS, EVENT_TIME_COLORS, HOUR_SLOT_HEIGHT, DEFAULT_SCROLL_HOUR } from "../../constants/agenda"
import { formatTime, parseWorkingHoursRange } from "../../utils/agenda"
import { CalendarAllDayEvents } from "./CalendarAllDayEvents"

const HOURS = Array.from({ length: 24 }, (_, i) => i)

function getEventPosition(event: CalendarEvent) {
  const [startHour, startMin] = event.start_time.split(":").map(Number)
  const endTime = event.end_time || `${startHour + 1}:00`
  const [endHour, endMin] = endTime.split(":").map(Number)

  const startMinutes = startHour * 60 + startMin
  const endMinutes = endHour * 60 + endMin
  const durationMinutes = endMinutes - startMinutes

  const top = (startMinutes / 60) * 60
  const height = (durationMinutes / 60) * 60

  return { top, height }
}

interface CalendarDayViewProps {
  currentDate: Date
  events: CalendarEvent[]
  onEventClick?: (event: CalendarEvent) => void
  onEmptyCellClick?: (date: Date) => void
  workingHours?: string
  highlightedEventId?: number | null
}

export function CalendarDayView({ currentDate, events, onEventClick, onEmptyCellClick, workingHours, highlightedEventId }: CalendarDayViewProps) {
  // Parse working hours from user settings
  const { startHour: workStartHour, endHour: workEndHour } = parseWorkingHoursRange(workingHours)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [canScrollUp, setCanScrollUp] = useState(false)
  const [canScrollDown, setCanScrollDown] = useState(true)

  // Filter events for current day
  const dayEvents = events.filter((event) => {
    const eventDate = new Date(event.start_date)
    return (
      eventDate.getDate() === currentDate.getDate() &&
      eventDate.getMonth() === currentDate.getMonth() &&
      eventDate.getFullYear() === currentDate.getFullYear()
    )
  })

  // Scroll to working hours start (or default) on mount
  useEffect(() => {
    if (scrollContainerRef.current) {
      const scrollTo = (workStartHour > 0 ? workStartHour - 1 : DEFAULT_SCROLL_HOUR) * HOUR_SLOT_HEIGHT
      scrollContainerRef.current.scrollTop = scrollTo
      updateScrollState()
    }
  }, [])

  const updateScrollState = () => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current
      setCanScrollUp(scrollTop > 0)
      setCanScrollDown(scrollTop < scrollHeight - clientHeight - 1)
    }
  }

  const scrollByHours = (hours: number) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        top: hours * HOUR_SLOT_HEIGHT,
        behavior: "smooth",
      })
    }
  }

  // Format the date header
  const days = ["domenica", "lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato"]
  const months = [
    "gennaio",
    "febbraio",
    "marzo",
    "aprile",
    "maggio",
    "giugno",
    "luglio",
    "agosto",
    "settembre",
    "ottobre",
    "novembre",
    "dicembre",
  ]
  const dateString = `${days[currentDate.getDay()]} ${currentDate.getDate()} ${months[currentDate.getMonth()]}`

  // Determine if current view is today
  const todayDate = new Date()
  const isToday =
    currentDate.getDate() === todayDate.getDate() &&
    currentDate.getMonth() === todayDate.getMonth() &&
    currentDate.getFullYear() === todayDate.getFullYear()

  // Scroll-into-view sull'evento evidenziato (es. dopo click su notifica)
  useEffect(() => {
    if (!highlightedEventId) return
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-event-id="${highlightedEventId}"]`)
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" })
    }, 300)
    return () => clearTimeout(timer)
  }, [highlightedEventId])

  // Current time line position (updates every minute)
  const [now, setNow] = useState(new Date())
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000)
    return () => clearInterval(interval)
  }, [])

  const currentTimeTop = isToday
    ? (now.getHours() * 60 + now.getMinutes()) / 60 * HOUR_SLOT_HEIGHT
    : 0

  return (
    <div className="min-w-0 bg-[var(--hexa-card-bg)] rounded-lg overflow-hidden hexa-section-shadow border border-[rgba(106,77,255,0.25)]">
      <CalendarAllDayEvents events={dayEvents} onEventClick={onEventClick} />
      {/* Header with date */}
      <div className="flex border-b border-[rgba(106,77,255,0.25)]">
        {/* Time column header with scroll arrows */}
        <div className="w-16 shrink-0 border-r border-[rgba(106,77,255,0.25)] flex flex-col items-center justify-center gap-0.5 py-2">
          <button
            onClick={() => scrollByHours(-2)}
            disabled={!canScrollUp}
            className="text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-secondary)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label="Scroll up"
          >
            <ChevronUp className="h-2.5 w-2.5" />
          </button>
          <button
            onClick={() => scrollByHours(2)}
            disabled={!canScrollDown}
            className="text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-secondary)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            aria-label="Scroll down"
          >
            <ChevronDown className="h-2.5 w-2.5" />
          </button>
        </div>

        {/* Day header */}
        <div className={`flex-1 text-center py-3 ${isToday ? "bg-[var(--hexa-purple)]/10 border-b-2 border-b-[var(--hexa-purple)]" : "bg-[var(--hexa-surface-elevated)]"}`}>
          <div className="text-[var(--hexa-text-primary)] font-semibold text-base capitalize">
            {dateString}{isToday && <span className="text-[var(--hexa-purple)] text-xs font-medium ml-2">(Oggi)</span>}
          </div>
        </div>
      </div>

      {/* Scrollable grid area */}
      <div
        ref={scrollContainerRef}
        onScroll={updateScrollState}
        className="agenda-timeline overflow-y-auto scrollbar-hexa"
      >
        <div className="relative flex">
          {/* Linee orizzontali per fascia orario lavorativo */}
          <div
            className="absolute left-16 right-0 h-[2px] pointer-events-none z-10"
            style={{
              top: `${workStartHour * 45}px`,
              backgroundColor: "var(--working-hours-line)",
            }}
          />
          <div
            className="absolute left-16 right-0 h-[2px] pointer-events-none z-10"
            style={{
              top: `${workEndHour * 45}px`,
              backgroundColor: "var(--working-hours-line)",
            }}
          />

          {/* Time column */}
          <div className="w-16 shrink-0 border-r border-[rgba(106,77,255,0.25)]">
            {HOURS.map((hour) => {
              const isWorkingHour = hour >= workStartHour && hour < workEndHour
              return (
                <div
                  key={hour}
                  className="h-[45px] border-b border-[rgba(106,77,255,0.25)] pr-4 pt-1 text-right flex items-start justify-end"
                  style={isWorkingHour ? {
                    borderLeft: "2px solid var(--working-hours-border)",
                  } : undefined}
                >
                  <span className={`text-[10px] ${isWorkingHour ? "text-[var(--hexa-text-secondary)]" : "text-[var(--hexa-text-muted)]"}`}>
                    {hour.toString().padStart(2, "0")}:00
                  </span>
                </div>
              )
            })}
          </div>

          {/* Day column with events */}
          <div className="flex-1 relative">
            {/* Hour lines */}
            {HOURS.map((hour) => {
              const isWorkingHour = hour >= workStartHour && hour < workEndHour
              return (
                <div
                  key={hour}
                  className="h-[45px] border-b border-[rgba(106,77,255,0.25)] cursor-pointer hover:bg-[rgba(106,77,255,0.08)] transition-colors"
                  style={isWorkingHour ? {
                    background: "var(--working-hours-bg)",
                    borderLeft: "2px solid var(--working-hours-border)",
                  } : undefined}
                  onClick={() => {
                    const clickedDate = new Date(currentDate)
                    clickedDate.setHours(hour, 0, 0, 0)
                    onEmptyCellClick?.(clickedDate)
                  }}
                />
              )
            })}

            {/* Events positioned absolutely */}
            {dayEvents.map((event) => {
              // Date-only events have no position in the hourly grid (all-day UI: S06).
              if (!event.start_time) return null
              const { top, height } = getEventPosition(event)
              const type = event.type || 'default'
              const bgColor = EVENT_COLORS_LIGHT[type] || EVENT_COLORS_LIGHT.default
              const borderColor = EVENT_COLORS[type] || EVENT_COLORS.default
              const textColor = EVENT_TEXT_COLORS[type] || EVENT_TEXT_COLORS.default
              const timeColor = EVENT_TIME_COLORS[type] || EVENT_TIME_COLORS.default
              return (
                <button
                  key={event.id}
                  data-event-id={event.id}
                  className={`absolute left-2 right-2 rounded-md px-2.5 py-1.5 text-left cursor-pointer hover:opacity-90 transition-opacity overflow-hidden ${highlightedEventId === event.id ? "task-highlight" : ""}`}
                  style={{
                    top: `${top * 0.75}px`,
                    height: `${height * 0.75}px`,
                    backgroundColor: bgColor,
                    borderLeft: `3px solid ${borderColor}`,
                  }}
                  onClick={(e) => { e.stopPropagation(); onEventClick?.(event) }}
                >
                  <div className="font-semibold text-xs leading-tight mb-0.5" style={{ color: textColor }}>
                    {event.title}
                  </div>
                  <div className="text-[10px]" style={{ color: timeColor }}>
                    {formatTime(event.start_time)}
                    {event.end_time && ` - ${formatTime(event.end_time)}`}
                  </div>
                  {event.location && (
                    <div className="text-[10px] mt-1 flex items-center gap-1" style={{ color: timeColor }}>
                      <svg
                        className="w-2.5 h-2.5 shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <span className="truncate">{event.location}</span>
                    </div>
                  )}
                </button>
              )
            })}

            {/* Current time line — only when viewing today */}
            {isToday && (
              <div
                className="absolute left-0 right-0 z-20 pointer-events-none"
                style={{ top: `${currentTimeTop}px` }}
              >
                <div
                  className="absolute rounded-full"
                  style={{
                    width: 8,
                    height: 8,
                    background: "var(--hexa-purple)",
                    left: -4,
                    top: -3,
                  }}
                />
                <div
                  className="w-full"
                  style={{
                    height: 2,
                    background: "var(--hexa-purple)",
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
